/**
 * Экран «Настройки» (`/settings`, ТЗ §8.7) — только для роли `chief`.
 * Исполнителя уводим на главную: сервер всё равно ответит 403, упираться в ошибку
 * на пустом экране незачем.
 *
 * Страница владеет данными всех четырёх вкладок (`GET/POST/PATCH /api/settings/*`),
 * вкладки получают готовые списки и обработчики — так одна перезагрузка кэша обновляет
 * и пользователей, и отделения, и маршруты, которые на них ссылаются.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    useCreateDepartment,
    useCreateUser,
    useMaxLink,
    useSaveRouting,
    useSettingsClinic,
    useSettingsDepartments,
    useSettingsRouting,
    useSettingsUsers,
    useUpdateClinic,
    useUpdateDepartment,
    useUpdateUser,
} from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState } from '@/components/ErrorState';
import { useToast } from '@/components/Toast';
import UsersTab from '@/components/settings/UsersTab';
import DepartmentsTab from '@/components/settings/DepartmentsTab';
import RoutingTab from '@/components/settings/RoutingTab';
import ClinicTab from '@/components/settings/ClinicTab';

type TabKey = 'users' | 'departments' | 'routing' | 'clinic';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
    { key: 'users', label: 'Пользователи' },
    { key: 'departments', label: 'Отделения' },
    { key: 'routing', label: 'Маршрутизация' },
    { key: 'clinic', label: 'Клиника' },
];

export default function SettingsPage() {
    const { status, isChief, clinic: authClinic } = useAuth();
    const toast = useToast();
    const [tab, setTab] = useState<TabKey>('users');

    const users = useSettingsUsers({ enabled: isChief });
    const departments = useSettingsDepartments({ enabled: isChief });
    const routing = useSettingsRouting({ enabled: isChief });
    const clinic = useSettingsClinic({ enabled: isChief });

    const createUser = useCreateUser();
    const updateUser = useUpdateUser();
    const createDepartment = useCreateDepartment();
    const updateDepartment = useUpdateDepartment();
    const saveRouting = useSaveRouting();
    const updateClinic = useUpdateClinic();
    const maxLink = useMaxLink();

    if (status === 'loading') {
        return <LoadingState label="Проверяем доступ…" />;
    }
    if (!isChief) {
        return <Navigate to="/" replace />;
    }

    const userItems = users.data?.items ?? [];
    const departmentItems = departments.data?.items ?? [];

    return (
        <>
            <div className="page-header">
                <h1>
                    ⚙️ Настройки
                    <span>{authClinic?.name ?? 'клиника'}</span>
                </h1>
                <span className="update-info">раздел доступен только главному врачу</span>
            </div>

            {/* `.seg`, а не `.top-tabs`: главное меню в layout уже занимает `.top-tabs`,
                и два одинаковых ряда подчёркнутых вкладок вставали бы друг под другом.
                В макете `.seg` — именно внутристраничный переключатель режима. */}
            <div className="seg" role="tablist">
                {TABS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === item.key}
                        className={tab === item.key ? 'active' : undefined}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === 'users' ? (
                <UsersTab
                    users={userItems}
                    departments={departmentItems}
                    loading={users.isPending}
                    error={users.error}
                    onCreate={async (input) => {
                        await createUser.mutateAsync(input);
                        toast('Пользователь создан');
                    }}
                    onUpdate={async (id, patch) => {
                        await updateUser.mutateAsync({ id, ...patch });
                        toast(patch.password ? 'Пароль изменён' : 'Пользователь сохранён');
                    }}
                    onMaxLink={() => maxLink.mutateAsync()}
                />
            ) : null}

            {tab === 'departments' ? (
                <DepartmentsTab
                    departments={departmentItems}
                    loading={departments.isPending}
                    error={departments.error}
                    onCreate={async (input) => {
                        await createDepartment.mutateAsync(input);
                        toast('Отделение создано');
                    }}
                    onUpdate={async (id, input) => {
                        await updateDepartment.mutateAsync({ id, ...input });
                        toast('Отделение сохранено');
                    }}
                />
            ) : null}

            {tab === 'routing' ? (
                <RoutingTab
                    rules={routing.data?.items ?? []}
                    executors={userItems}
                    departments={departmentItems}
                    loading={routing.isPending}
                    error={routing.error}
                    onSave={async (items) => {
                        await saveRouting.mutateAsync({ items });
                        toast('Маршрутизация сохранена');
                    }}
                />
            ) : null}

            {tab === 'clinic' ? (
                <ClinicTab
                    clinic={clinic.data?.clinic ?? null}
                    loading={clinic.isPending}
                    error={clinic.error}
                    onSave={async (patch) => {
                        await updateClinic.mutateAsync(patch);
                        toast('Настройки клиники сохранены');
                    }}
                />
            ) : null}
        </>
    );
}
